import { inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withMethods,
  withState,
} from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { CampaignsService } from '../services/campaigns.service';
import { Campaign } from '../types/campaign-types';

type LoadStatus = 'idle' | 'loading' | 'loaded' | 'error';

type CampaignState = {
  campaigns: Campaign[];
  status: LoadStatus;
};

const initialState: CampaignState = {
  campaigns: [],
  status: 'idle',
};

export const CampaignStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withMethods((store, campaignsService = inject(CampaignsService)) => {
    async function fetchFromApi(): Promise<void> {
      patchState(store, { status: 'loading' });
      try {
        const campaigns = await firstValueFrom(campaignsService.getActiveCampaigns());
        patchState(store, { campaigns: campaigns ?? [], status: 'loaded' });
      } catch {
        patchState(store, { status: 'error' });
      }
    }

    return {
      /** Fetch active campaigns. No-ops if already loaded/loading. */
      async fetch(): Promise<void> {
        if (store.status() === 'loading' || store.status() === 'loaded') return;
        await fetchFromApi();
      },

      /** Force a fresh fetch regardless of current status. */
      async refresh(): Promise<void> {
        await fetchFromApi();
      },

      clear(): void {
        patchState(store, { ...initialState });
      },
    };
  }),
);
